<template>
  <AppLayout>
    <div class="flex flex-col items-center gap-4">
      <h1>Device Sessions</h1>

      <div class="flex gap-4 w-full max-w-2xl">
        <div class="flex-1">
          <label for="org-select" class="block text-sm font-medium mb-2">Organization</label>
          <Select
            id="org-select"
            v-model="selectedOrgId"
            :options="organizationOptions"
            optionLabel="name"
            optionValue="id"
            placeholder="All organizations"
            class="w-full"
            filter
          >
            <template #option="{ option }">
              <div>
                {{ option.name }}
                <span v-if="option.code" class="text-surface-400 ml-2">{{ option.code }}</span>
              </div>
            </template>
          </Select>
        </div>

        <div class="flex-1">
          <label for="email-filter" class="block text-sm font-medium mb-2">User email</label>
          <InputText
            id="email-filter"
            v-model="emailFilter"
            placeholder="Filter by email..."
            class="w-full"
          />
        </div>
      </div>

      <DataTable
        :value="sessions"
        :loading="loading"
        paginator
        :rows="50"
        :rowsPerPageOptions="[50, 100, 200]"
        :totalRecords="totalRecords"
        :lazy="true"
        @page="onPage"
        tableStyle="min-width: 50rem"
      >
        <Column header="User">
          <template #body="{ data }">
            <span v-if="data.userEmail">{{ data.userEmail }}</span>
            <span v-else class="text-surface-400 italic">(anonymous)</span>
          </template>
        </Column>
        <Column header="Organization">
          <template #body="{ data }">
            <span v-if="data.userEmail">{{ data.orgName }}</span>
            <span v-else class="text-surface-400 italic">{{ data.orgName }}</span>
          </template>
        </Column>
        <Column header="Device">
          <template #body="{ data }">
            <span v-if="data.device">{{ data.device }}</span>
            <span v-else class="text-surface-400 italic">(web)</span>
          </template>
        </Column>
        <Column field="appVersion" header="App Version" />
        <Column field="systemVersion" header="System Version" />
        <Column header="Last Seen">
          <template #body="{ data }">
            {{ formatDate(data.lastSeen) }}
          </template>
        </Column>
        <Column header="First Seen">
          <template #body="{ data }">
            {{ formatDate(data.firstSeen) }}
          </template>
        </Column>
      </DataTable>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import dayjs from 'dayjs';
import type { DeviceSessionDto, OrganizationDto } from '@/api/generated';
import AppLayout from '@/components/AppLayout.vue';
import { useApiClient } from '@/api/apiClient';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';

const { api } = useApiClient();

const sessions = ref<DeviceSessionDto[]>([]);
const loading = ref(false);
const totalRecords = ref(0);
const currentOffset = ref(0);
const currentLimit = ref(50);

const selectedOrgId = ref<string>('');
const emailFilter = ref('');
const organizations = ref<OrganizationDto[]>([]);

const organizationOptions = computed(() => [
  { id: '', name: 'All organizations', code: '' },
  ...organizations.value.map(org => ({ id: org.id, name: org.name, code: org.code })),
]);

function formatDate(date: string): string {
  return dayjs(date).format('MMM D, YYYY HH:mm');
}

async function loadSessions() {
  loading.value = true;
  try {
    const { data, error } = await api.adminDeviceSessionsControllerList({
      query: {
        orgId: selectedOrgId.value || undefined,
        email: emailFilter.value || undefined,
        limit: String(currentLimit.value),
        offset: String(currentOffset.value),
      },
    });
    if (error) {
      throw new Error('Failed to load device sessions');
    }
    sessions.value = data?.data ?? [];
    totalRecords.value = data?.total ?? 0;
  } finally {
    loading.value = false;
  }
}

async function loadOrganizations() {
  const { data, error } = await api.adminOrganizationControllerList();
  if (error) {
    throw new Error('Failed to load organizations');
  }
  organizations.value = data ?? [];
}

function onPage(event: { first: number; rows: number }) {
  currentOffset.value = event.first;
  currentLimit.value = event.rows;
  loadSessions();
}

const debouncedLoadSessions = useDebounceFn(() => {
  currentOffset.value = 0;
  loadSessions();
}, 300);

watch(selectedOrgId, () => {
  currentOffset.value = 0;
  loadSessions();
});

watch(emailFilter, () => {
  debouncedLoadSessions();
});

loadSessions();
loadOrganizations();
</script>
